// Lightweight searchable combobox with two modes:
//
// 1. Client-side (default). Renders all options up-front; filters them
//    locally when the user types.
//
//    <div data-combobox>
//      <button type="button" data-combobox-trigger>
//        <span data-combobox-display data-placeholder="Pick one">Pick one</span>
//      </button>
//      <div data-combobox-panel hidden>
//        <input type="text" data-combobox-search />
//        <ul data-combobox-list>
//          <li data-combobox-option data-value="1" data-search="...">…</li>
//        </ul>
//        <div data-combobox-empty hidden>Nothing</div>
//      </div>
//      <input type="hidden" name="…" value="…" data-combobox-input />
//    </div>
//
// 2. Remote (typeahead). Add `data-combobox-url` and an option template.
//    The list starts empty; on input, the combobox fetches JSON and renders
//    options via the template. Preselected values use `data-combobox-initial`.
//
//    <div data-combobox
//         data-combobox-url="/students/search.json"
//         data-combobox-initial='{"label":"María Rojas","meta":"…","initial":"M"}'>
//      …same chrome, plus:
//      <template data-combobox-option-template>
//        <li data-combobox-option role="option">
//          <div data-slot="initial"></div>
//          <div>
//            <div data-slot="label"></div>
//            <div data-slot="meta"></div>
//          </div>
//        </li>
//      </template>
//      <div data-combobox-loading hidden>Buscando…</div>
//      <div data-combobox-hint>Escribí para buscar</div>
//    </div>
//
//    The JSON endpoint must return `[{ id, label, meta, initial }, …]`.

const SELECTOR = "[data-combobox]"
const DEBOUNCE_MS = 200

function init(root) {
  if (root.dataset.comboboxInit === "1") return
  root.dataset.comboboxInit = "1"

  const trigger = root.querySelector("[data-combobox-trigger]")
  const panel = root.querySelector("[data-combobox-panel]")
  const search = root.querySelector("[data-combobox-search]")
  const list = root.querySelector("[data-combobox-list]")
  const empty = root.querySelector("[data-combobox-empty]")
  const loading = root.querySelector("[data-combobox-loading]")
  const hint = root.querySelector("[data-combobox-hint]")
  const hidden = root.querySelector("[data-combobox-input]")
  const display = root.querySelector("[data-combobox-display]")
  const template = root.querySelector("[data-combobox-option-template]")
  if (!trigger || !panel || !list || !hidden) return

  const remoteUrl = root.dataset.comboboxUrl || null
  const remoteMinChars = Number(root.dataset.comboboxMinChars || 3)
  const placeholder = display?.dataset.placeholder || display?.textContent || ""

  const renderSelection = (option) => {
    if (!display) return
    if (option) {
      const clone = option.cloneNode(true)
      clone.removeAttribute("data-combobox-option")
      clone.removeAttribute("data-value")
      clone.removeAttribute("data-search")
      display.innerHTML = clone.innerHTML
      display.classList.remove("text-ink-subtle")
    } else {
      display.textContent = placeholder
      display.classList.add("text-ink-subtle")
    }
  }

  const setSelected = (option) => {
    list.querySelectorAll("[data-combobox-option][aria-selected='true']")
      .forEach((el) => el.setAttribute("aria-selected", "false"))
    if (option) {
      option.setAttribute("aria-selected", "true")
      hidden.value = option.dataset.value || ""
    } else {
      hidden.value = ""
    }
    renderSelection(option)
    hidden.dispatchEvent(new Event("change", { bubbles: true }))
  }

  const buildOption = (data) => {
    if (!template) return null
    const fragment = template.content.cloneNode(true)
    const li = fragment.querySelector("[data-combobox-option]")
    if (!li) return null
    li.dataset.value = data.id
    li.dataset.search = (data.label || "").toLowerCase()
    li.setAttribute("aria-selected", "false")
    li.querySelectorAll("[data-slot]").forEach((slot) => {
      const key = slot.dataset.slot
      const value = data[key]
      if (value == null || value === "") {
        slot.hidden = true
      } else {
        slot.textContent = value
      }
    })
    return li
  }

  const open = () => {
    panel.hidden = false
    trigger.setAttribute("aria-expanded", "true")
    if (search) {
      if (!remoteUrl) {
        search.value = ""
        filterLocal("")
      }
      requestAnimationFrame(() => search.focus())
    }
  }

  const close = () => {
    panel.hidden = true
    trigger.setAttribute("aria-expanded", "false")
  }

  const toggle = () => (panel.hidden ? open() : close())

  const filterLocal = (query) => {
    const q = query.trim().toLowerCase()
    let visible = 0
    list.querySelectorAll("[data-combobox-option]").forEach((opt) => {
      const haystack = opt.dataset.search || opt.textContent.toLowerCase()
      const match = q === "" || haystack.includes(q)
      opt.hidden = !match
      if (match) visible += 1
    })
    if (empty) empty.hidden = visible !== 0
  }

  // ── Remote mode ───────────────────────────────────────────────────────
  let fetchController = null
  let debounceTimer = null

  const renderRemoteResults = (results) => {
    list.replaceChildren()
    results.forEach((data) => {
      const li = buildOption(data)
      if (li) list.appendChild(li)
    })
    const hasResults = results.length > 0
    if (empty) empty.hidden = hasResults
    if (hint) hint.hidden = true
  }

  const remoteSearch = (query) => {
    if (!remoteUrl) return
    if (fetchController) fetchController.abort()
    fetchController = new AbortController()
    if (loading) loading.hidden = false
    if (empty) empty.hidden = true
    if (hint) hint.hidden = true

    const url = new URL(remoteUrl, window.location.origin)
    url.searchParams.set("q", query)

    fetch(url.toString(), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: fetchController.signal,
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(res.statusText))))
      .then((results) => {
        if (loading) loading.hidden = true
        renderRemoteResults(results || [])
      })
      .catch((err) => {
        if (err.name === "AbortError") return
        if (loading) loading.hidden = true
        if (empty) empty.hidden = false
      })
  }

  // ── Event wiring ──────────────────────────────────────────────────────
  trigger.addEventListener("click", (event) => {
    event.preventDefault()
    toggle()
  })

  if (search) {
    search.addEventListener("input", () => {
      if (remoteUrl) {
        clearTimeout(debounceTimer)
        if (fetchController) fetchController.abort()
        const value = search.value.trim()
        if (value.length < remoteMinChars) {
          list.replaceChildren()
          if (loading) loading.hidden = true
          if (empty) empty.hidden = true
          if (hint) hint.hidden = false
          return
        }
        if (hint) hint.hidden = true
        debounceTimer = setTimeout(() => remoteSearch(value), DEBOUNCE_MS)
      } else {
        filterLocal(search.value)
      }
    })
    search.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault()
        close()
        trigger.focus()
      }
    })
  }

  list.addEventListener("click", (event) => {
    const option = event.target.closest("[data-combobox-option]")
    if (!option || !list.contains(option)) return
    event.preventDefault()
    setSelected(option)
    close()
    trigger.focus()
  })

  document.addEventListener("click", (event) => {
    if (panel.hidden) return
    if (!root.contains(event.target)) close()
  })

  // ── Initial selection ─────────────────────────────────────────────────
  if (remoteUrl) {
    const initialData = root.dataset.comboboxInitial
    if (hidden.value && initialData) {
      try {
        const parsed = JSON.parse(initialData)
        const li = buildOption({ id: hidden.value, ...parsed })
        if (li) {
          list.appendChild(li)
          setSelected(li)
        }
      } catch (e) {
        // ignore malformed initial payload
      }
    } else {
      renderSelection(null)
    }
  } else {
    const initial = hidden.value
      ? list.querySelector(`[data-combobox-option][data-value="${CSS.escape(hidden.value)}"]`)
      : null
    if (initial) {
      setSelected(initial)
    } else {
      renderSelection(null)
    }
  }
}

function initAll(scope = document) {
  scope.querySelectorAll(SELECTOR).forEach(init)
}

document.addEventListener("DOMContentLoaded", () => initAll())

document.addEventListener("combobox:init", (event) => {
  const scope = event.detail?.scope || document
  initAll(scope)
})
